import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/toast';
import { useAuth } from '../contexts/AuthContext';

/**
 * 관리자 로그인 페이지
 * Electron 앱에서만 접근 가능한 관리자 로그인 화면입니다.
 */
const AdminLogin: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { login } = useAuth();

  // 입력값 변경 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 로그인 처리
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      showToast('아이디와 비밀번호를 입력해주세요.', 'error');
      return;
    }

    setIsLoading(true);
    
    try {
      // 임시 관리자 계정 (실제 환경에서는 서버 인증 필요)
      const isValidLogin = (
        formData.username === 'admin' && formData.password === 'exhibition2024'
      ) || (
        formData.username === 'manager' && formData.password === 'manager123'
      );

      if (isValidLogin) {
        await login(formData.username);
        showToast('로그인 성공! 관리자 대시보드로 이동합니다.', 'success');
        navigate('/admin');
      } else {
        showToast('아이디 또는 비밀번호가 올바르지 않습니다.', 'error');
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      showToast('로그인 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Enter 키 처리
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin(e as any);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* 로고 및 제목 */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <img 
              src="/logo_center_420.png" 
              alt="전시회 관리 시스템" 
              className="h-20 w-auto mx-auto"
            />
          </div>
        </div>

        {/* 로그인 폼 */}
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">관리자 로그인</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {/* 아이디 입력 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  아이디
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="관리자 아이디를 입력하세요"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>

              {/* 비밀번호 입력 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="비밀번호를 입력하세요"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>

              {/* 로그인 버튼 */}
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    로그인 중...
                  </div>
                ) : (
                  '로그인'
                )}
              </Button>
            </form>

          
          </CardContent>
        </Card>

        {/* 푸터 */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>전시회 현장등록 관리시스템 v1.0</p>
          <p>@UrbanComplex</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 